<script setup>
import { reactive, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { useI18n } from 'vue-i18n';
import { getFieldVariables } from '../../helpers/automationUtils';

import DynamicField from '../dynamicField/Index.vue';

const props = defineProps({
  inputMapping: {
    type: Object,
    default: () => ({}),
  },
  inputMappingFields: {
    type: Array,
    default: () => [],
  },
  dirty: {
    type: Boolean,
    default: false,
  },
  variables: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['change']);

const { t: $t } = useI18n();

const state = reactive({
  updatedInputMapping: props.inputMapping,
});

const getVariables = (inputMapping, inputMappingFields) => {
  const variables = getFieldVariables(inputMapping, inputMappingFields);
  const variableNames = variables.map(({ name }) => name);
  return variableNames;
};

const rules = {
  updatedInputMapping: {
    duplicate: value => {
      const { inputMappingFields, variables } = props;
      const inheritedVariables = variables.map(({ name }) => name);
      const inputMappingVariables = getVariables(value, inputMappingFields);
      // Expect that input mapping variables also exist in inherited variables
      const allVariables = [...inheritedVariables, ...inputMappingVariables];
      const duplicateVariables = allVariables
        .filter(v => v)
        .filter((item, i, items) => {
          const duplicates = items.filter(v => v === item);
          return duplicates.length > 2;
        });
      return duplicateVariables.length === 0;
    },
  },
};

const v$ = useVuelidate(rules, state);
if (props.dirty) {
  v$.value.$validate();
}

watch(
  () => props.inputMapping,
  value => {
    state.updatedInputMapping = value;
  },
  { immediate: true }
);

const getField = fieldName => {
  return props.inputMappingFields.find(({ name }) => name === fieldName);
};

const isVisible = field => {
  if (!field.show_if) return true;

  return field.show_if.every(({ field: fieldName, value }) => {
    const currentValue = state.step[fieldName] || getField(fieldName)?.default;
    if (!currentValue && currentValue !== 0 && currentValue !== false)
      return false;
    const values = Array.isArray(value) ? value : [value];
    return values.some(v => {
      if (v.includes('*')) {
        return `${currentValue}`.includes(v.replace(/\*/g, ''));
      }
      return currentValue === v;
    });
  });
};

const onInputMappingFieldChange = (key, value) => {
  const inputMappingBefore = state.updatedInputMapping ?? {};
  const inputMapping = {
    ...inputMappingBefore,
    [key]: value,
  };
  state.updatedInputMapping = inputMapping;
  v$.value.updatedInputMapping.$touch();
  emit('change', inputMapping);
};
</script>

<template>
  <div class="input-mapping-fields">
    <div
      class="rounded-xl flex flex-col gap-3"
      :class="{ 'input-mapping-error': v$.updatedInputMapping.$error }"
    >
      <DynamicField
        v-for="(field, index) of inputMappingFields"
        :key="index"
        :field="field"
        :value="state.updatedInputMapping[field.name]"
        :visible="isVisible(field)"
        :show-variables-selector="false"
        :dirty="dirty"
        @change="value => onInputMappingFieldChange(field.name, value)"
      />

      <p
        v-if="v$.updatedInputMapping.$error"
        class="min-w-0 mb-0 text-xs truncate transition-all duration-500 ease-in-out text-n-ruby-9 dark:text-n-ruby-9"
      >
        <template v-if="v$.updatedInputMapping.duplicate.$invalid">
          {{
            $t(
              'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.ACTIONS.INPUT_MAPPING.ERRORS.DUPLICATE'
            )
          }}
        </template>
        <template v-else>
          {{
            $t(
              'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.ACTIONS.INPUT_MAPPING.ERRORS.INVALID'
            )
          }}
        </template>
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.input-mapping-error {
  @apply p-2 my-2 bg-n-ruby-5 mb-4;
}
</style>
